import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../../../api/client";
import MetricCard from "../components/MetricCard";
import StatusBadgeBase from "../components/StatusBadge";
import ToastBase from "../components/Toast";
import Tabs from "../components/Tabs";
import InventoryReportTab from "./reports/InventoryReportTab";
import PartWiseReportModal from "./reports/PartWiseReportModal";
import PnlReportTab from "./reports/PnlReportTab";
import AssetReportTab from "./reports/AssetReportTab";
import { PERMISSIONS } from "../../../constants/permissions";
import { usePermission } from "../../../hooks/usePermission";
import useAuth from "../../../hooks/useAuth";

const STATUS_BADGE = {
  in_progress: { bg: "#dbeafe", color: "#1d4ed8", label: "On Track" },
  at_risk: { bg: "#fff7ed", color: "#c2410c", label: "At Risk" },
  overdue: { bg: "#fee2e2", color: "#dc2626", label: "Overdue" },
  completed: { bg: "#dcfce7", color: "#15803d", label: "Completed" },
  pending: { bg: "#f1f5f9", color: "#475569", label: "Pending" },
};

function StatusBadge({ status }) {
  return <StatusBadgeBase status={status} colorMap={STATUS_BADGE} />;
}

function SortTh({ label, colKey, sort, onSort, stickyBg, borderColor }) {
  const active = sort.col === colKey;
  return (
    <th
      onClick={() => onSort(colKey)}
      style={{
        padding: "10px 16px",
        textAlign: "left",
        fontWeight: 700,
        color: active ? "#0f2d5c" : "#374151",
        borderBottom: `1px solid ${borderColor || "#e4ebf5"}`,
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
        background: stickyBg || "#f8fafc",
        zIndex: 1,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {label}
      <span
        style={{
          marginLeft: 4,
          fontSize: 10,
          color: active ? "#0f2d5c" : "#cbd5e1",
        }}
      >
        {active ? (sort.dir === "asc" ? "▲" : "▼") : "⬍"}
      </span>
    </th>
  );
}

function formatTime(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function formatDateLabel(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
}

function sortRows(rows, sort) {
  if (!sort.col) return rows;
  return [...rows].sort((a, b) => {
    let av = a[sort.col],
      bv = b[sort.col];
    if (av == null) av = "";
    if (bv == null) bv = "";
    const cmp =
      typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sort.dir === "asc" ? cmp : -cmp;
  });
}

function useSort() {
  const [sort, setSort] = useState({ col: null, dir: "asc" });
  const onSort = (col) =>
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" },
    );
  return [sort, onSort];
}

const Toast = ToastBase;

const ACTIVITY_COLS = [
  { label: "PO Number", key: "poNumber" },
  { label: "Stage", key: "stage" },
  { label: "Worker", key: "worker" },
  { label: "Parts Reported", key: "partsReported" },
  { label: "Cumulative Done", key: "cumulativeDone" },
  { label: "Remaining", key: "remaining" },
  { label: "Status", key: "status" },
  { label: "Time", key: "time" },
];

const OVERDUE_COLS = [
  { label: "PO Number", key: "poNumber" },
  { label: "Part Type", key: "partType" },
  { label: "Stage", key: "stage" },
  { label: "Worker", key: "worker" },
  { label: "Days Overdue", key: "daysOverdue" },
  { label: "Status", key: "status" },
];

function POReportTab() {
  const todayStr = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(todayStr);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(null);
  const [toast, setToast] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [partWiseOpen, setPartWiseOpen] = useState(false);

  const [activitySearch, setActivitySearch] = useState("");
  const [overdueSearch, setOverdueSearch] = useState("");
  const [activitySort, onActivitySort] = useSort();
  const [overdueSort, onOverdueSort] = useSort();

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await api.get(`/manufacturing/reports/daily?date=${d}`);
      setData(res.data.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(date);
  }, [date, load]);

  const goBack = () => setDate(addDays(date, -1));
  const goForward = () => {
    const n = addDays(date, 1);
    if (n <= todayStr) setDate(n);
  };

  const filteredActivity = useMemo(() => {
    const rows = data?.todayActivity ?? [];
    const q = activitySearch.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) =>
          [r.poNumber, r.stage, r.worker, r.status].some((v) =>
            String(v ?? "")
              .toLowerCase()
              .includes(q),
          ),
        )
      : rows;
    return sortRows(filtered, activitySort);
  }, [data, activitySearch, activitySort]);

  const filteredOverdue = useMemo(() => {
    const rows = data?.overdueAndAtRisk ?? [];
    const q = overdueSearch.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) =>
          [r.poNumber, r.partType, r.stage, r.worker, r.status].some((v) =>
            String(v ?? "")
              .toLowerCase()
              .includes(q),
          ),
        )
      : rows;
    return sortRows(filtered, overdueSort);
  }, [data, overdueSearch, overdueSort]);

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    try {
      const res = await api.get(`/manufacturing/reports/export?date=${date}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `daily-status-report-${date}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setToast({ msg: "Failed to download report.", type: "error" });
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleNotify = async (stageProgressId) => {
    if (!stageProgressId) {
      setToast({ msg: "No stage assigned for this PO yet.", type: "error" });
      return;
    }
    setNotifying(stageProgressId);
    try {
      await api.post(`/manufacturing/stages/${stageProgressId}/notify`);
      setToast({ msg: "Notification sent successfully.", type: "success" });
    } catch (err) {
      setToast({
        msg: err.response?.data?.error || "Failed to send notification.",
        type: "error",
      });
    } finally {
      setNotifying(null);
    }
  };

  const searchInputStyle = {
    padding: "5px 10px",
    border: "1px solid #e4ebf5",
    borderRadius: 7,
    fontSize: 13,
    color: "#374151",
    outline: "none",
    width: 200,
    background: "#f8fafc",
  };

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ margin: 0, color: "#0f2d5c" }}>
            Daily Report
            <span
              style={{
                fontWeight: 400,
                color: "#64748b",
                fontSize: 18,
                marginLeft: 10,
              }}
            >
              — {formatDateLabel(date)}
            </span>
          </h1>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "#fff",
              border: "1px solid #e4ebf5",
              borderRadius: 8,
              padding: "5px 10px",
            }}
          >
            <button
              onClick={goBack}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#64748b",
                fontSize: 16,
                padding: "0 4px",
                lineHeight: 1,
              }}
              title="Previous day"
            >
              ‹
            </button>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#0f2d5c",
                minWidth: 100,
                textAlign: "center",
              }}
            >
              {formatDateLabel(date)}
            </span>
            <button
              onClick={goForward}
              disabled={date >= todayStr}
              style={{
                background: "none",
                border: "none",
                cursor: date >= todayStr ? "default" : "pointer",
                color: date >= todayStr ? "#d1d5db" : "#64748b",
                fontSize: 16,
                padding: "0 4px",
                lineHeight: 1,
              }}
              title="Next day"
            >
              ›
            </button>
          </div>
          {date !== todayStr && (
            <button
              onClick={() => setDate(todayStr)}
              style={{
                padding: "6px 12px",
                border: "1px solid #d6dfed",
                borderRadius: 8,
                background: "#fff",
                cursor: "pointer",
                fontSize: 13,
                color: "#64748b",
                fontWeight: 600,
              }}
            >
              Today
            </button>
          )}
          {date === todayStr && (
            <button
              className="btn-primary"
              style={{ fontSize: 13 }}
              onClick={handleDownloadReport}
              disabled={downloadingReport}
            >
              {downloadingReport ? "Preparing…" : "Download Report"}
            </button>
          )}
          <button
            onClick={() => setPartWiseOpen(true)}
            style={{
              padding: "6px 12px",
              border: "1px solid #d6dfed",
              borderRadius: 8,
              background: "#fff",
              cursor: "pointer",
              fontSize: 13,
              color: "#1d4ed8",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            Download Part-wise Report
          </button>
        </div>
      </div>

      {/* Metric cards */}
      <div
        style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}
      >
        <MetricCard
          label="Total Parts Moved Today"
          value={
            loading ? "—" : data?.summary?.totalPartsMoved?.toLocaleString()
          }
        />
        <MetricCard
          label="POs Active"
          value={loading ? "—" : data?.summary?.activePos}
        />
        <MetricCard
          label="Stages Completed Today"
          value={loading ? "—" : data?.summary?.stagesCompleted}
        />
        <MetricCard
          label="Overdue Flags"
          value={loading ? "—" : data?.summary?.overdueFlags}
          accent={data?.summary?.overdueFlags > 0 ? "#dc2626" : undefined}
        />
      </div>

      {/* Overdue & At Risk */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #fca5a5",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            padding: "12px 20px",
            borderBottom: "1px solid #fca5a5",
            background: "#fff5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <h2 style={{ margin: 0, fontSize: 15, color: "#dc2626" }}>
            Overdue &amp; At Risk
          </h2>
          <input
            type="text"
            placeholder="Search PO, stage, worker…"
            value={overdueSearch}
            onChange={(e) => setOverdueSearch(e.target.value)}
            style={{ ...searchInputStyle, border: "1px solid #fca5a5" }}
          />
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>
            Loading…
          </div>
        ) : !data?.overdueAndAtRisk?.length ? (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: "#15803d",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            No overdue or at risk jobs. Great work! ✓
          </div>
        ) : (
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
          >
            <thead>
              <tr style={{ background: "#fef2f2" }}>
                {OVERDUE_COLS.map(({ label, key }) => (
                  <SortTh
                    key={key}
                    label={label}
                    colKey={key}
                    sort={overdueSort}
                    onSort={onOverdueSort}
                    stickyBg="#fef2f2"
                    borderColor="#fca5a5"
                  />
                ))}
                <th
                  style={{
                    padding: "10px 16px",
                    textAlign: "left",
                    fontWeight: 700,
                    color: "#374151",
                    borderBottom: "1px solid #fca5a5",
                    whiteSpace: "nowrap",
                    position: "sticky",
                    top: 0,
                    background: "#fef2f2",
                    zIndex: 1,
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredOverdue.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      padding: "24px 16px",
                      textAlign: "center",
                      color: "#94a3b8",
                      fontSize: 13,
                    }}
                  >
                    No results match your search.
                  </td>
                </tr>
              ) : (
                filteredOverdue.map((row, i) => (
                  <tr
                    key={i}
                    style={{
                      borderBottom: "1px solid #fef2f2",
                      background: "#fff",
                    }}
                  >
                    <td style={{ padding: "10px 16px" }}>
                      <Link
                        to={`/manufacturing/po/${row.poId}`}
                        style={{
                          color: "#1d4ed8",
                          fontWeight: 700,
                          textDecoration: "none",
                        }}
                      >
                        {row.poNumber}
                      </Link>
                    </td>
                    <td style={{ padding: "10px 16px", color: "#64748b" }}>
                      {row.partType}
                    </td>
                    <td style={{ padding: "10px 16px", color: "#64748b" }}>
                      {row.stage}
                    </td>
                    <td style={{ padding: "10px 16px", color: "#0f2d5c" }}>
                      {row.worker}
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <span style={{ color: "#dc2626", fontWeight: 700 }}>
                        {row.daysOverdue > 0
                          ? `${row.daysOverdue} day${row.daysOverdue !== 1 ? "s" : ""}`
                          : "—"}
                      </span>
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <StatusBadge status={row.status} />
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <button
                        onClick={() => handleNotify(row.stageProgressId)}
                        disabled={
                          !row.stageProgressId ||
                          notifying === row.stageProgressId
                        }
                        style={{
                          padding: "4px 12px",
                          border: "1px solid #d6dfed",
                          borderRadius: 6,
                          background: "#f8fafc",
                          cursor: row.stageProgressId ? "pointer" : "default",
                          color: "#475569",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {notifying === row.stageProgressId
                          ? "Sending…"
                          : "Notify"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Today's Activity */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e4ebf5",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            padding: "12px 20px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <h2 style={{ margin: 0, fontSize: 15, color: "#0f2d5c" }}>
            Today's Activity
          </h2>
          <input
            type="text"
            placeholder="Search PO, stage, worker…"
            value={activitySearch}
            onChange={(e) => setActivitySearch(e.target.value)}
            style={searchInputStyle}
          />
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>
            Loading…
          </div>
        ) : !data?.todayActivity?.length ? (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: "#94a3b8",
              fontSize: 14,
            }}
          >
            No activity recorded for this date.
          </div>
        ) : (
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {ACTIVITY_COLS.map(({ label, key }) => (
                    <SortTh
                      key={key}
                      label={label}
                      colKey={key}
                      sort={activitySort}
                      onSort={onActivitySort}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredActivity.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        padding: "24px 16px",
                        textAlign: "center",
                        color: "#94a3b8",
                        fontSize: 13,
                      }}
                    >
                      No results match your search.
                    </td>
                  </tr>
                ) : (
                  filteredActivity.map((row, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "10px 16px" }}>
                        <Link
                          to={`/manufacturing/po/${row.poId}`}
                          style={{
                            color: "#1d4ed8",
                            fontWeight: 700,
                            textDecoration: "none",
                          }}
                        >
                          {row.poNumber}
                        </Link>
                      </td>
                      <td style={{ padding: "10px 16px", color: "#64748b" }}>
                        {row.stage}
                      </td>
                      <td style={{ padding: "10px 16px", color: "#0f2d5c" }}>
                        {row.worker}
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          color: "#7c3aed",
                          fontWeight: 700,
                        }}
                      >
                        {row.partsReported?.toLocaleString()}
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          color: "#15803d",
                          fontWeight: 600,
                        }}
                      >
                        {row.cumulativeDone?.toLocaleString()}
                      </td>
                      <td style={{ padding: "10px 16px", color: "#0f2d5c" }}>
                        {row.remaining?.toLocaleString()}
                      </td>
                      <td style={{ padding: "10px 16px" }}>
                        <StatusBadge status={row.status} />
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          color: "#94a3b8",
                          fontSize: 12,
                        }}
                      >
                        {formatTime(row.time)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {partWiseOpen && (
        <PartWiseReportModal onClose={() => setPartWiseOpen(false)} />
      )}

      {toast && (
        <Toast
          msg={toast.msg}
          type={toast.type}
          onDone={() => setToast(null)}
        />
      )}
    </div>
  );
}

const REPORTS_TABS = [
  { key: "po", label: "PO" },
  { key: "inventory", label: "Inventory" },
];
// Money figures — only for holders of a P&L permission
const PNL_TAB = { key: "pnl", label: "P&L" };
// Only when the superadmin has Asset Management on, for people who can see maintenance
const ASSETS_TAB = { key: "assets", label: "Assets" };

export default function Reports() {
  const [tab, setTab] = useState("po");
  const canSeePnl = usePermission([
    PERMISSIONS.MANUFACTURING_PNL_VIEW,
    PERMISSIONS.MANUFACTURING_PNL_MANAGE,
  ]);
  const { enabledModules } = useAuth();
  const canSeeAssets =
    usePermission([
      PERMISSIONS.MANUFACTURING_ASSET_VIEW,
      PERMISSIONS.MANUFACTURING_ASSET_MAINTAIN,
      PERMISSIONS.MANUFACTURING_ASSET_MANAGE,
    ]) && Boolean(enabledModules?.includes("assets"));
  const tabs = [...REPORTS_TABS, ...(canSeePnl ? [PNL_TAB] : []), ...(canSeeAssets ? [ASSETS_TAB] : [])];
  return (
    <div
      style={{
        padding: 20,
        height: "100%",
        overflow: "auto",
        background: "#f5f8fc",
      }}
    >
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === "po" && <POReportTab />}
      {tab === "inventory" && <InventoryReportTab />}
      {tab === "pnl" && canSeePnl && <PnlReportTab />}
      {tab === "assets" && canSeeAssets && <AssetReportTab />}
    </div>
  );
}
